import { useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppContext } from "../context/app.context";
import {
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_APIKEY,
  MONGODB_DATASOURCE_TS,
  MONGODB_COLLECTION_STORES,
} from "../utility/keys";

// - - - - - - - - - -
// #162 (Brandon, 2026-05-27) — fetch every Lowe's store associated with
// a KI location, so the CrewDetailModal's Capacities tab can render the
// per-installer "Associated Stores" CHECKBOX grid (rather than the
// initial v05-27-2026-01 text list).
//
// Universe = every doc in the `Stores` collection whose
// `KI_associatedLocation` field equals the installer's home location
// code (e.g. "BHM"). Returns the raw `Store#` values from each doc;
// downstream helpers normalize for comparison against the installer's
// `capacity_stores` list (FileMaker zero-pads to 4 chars, Mongo stores
// raw integers — both need parsing to integer-string for matching).
//
// Cache strategy: staleTime 5 minutes (the Stores collection is admin-
// managed and rarely changes). Hook is mounted INSIDE CrewDetailModal,
// so it unmounts when the modal closes; refetchOnMount: "always" gives
// fresh data per modal-open without polling while the modal is open.
//
// Returns:
//   {
//     stores: Array<{ "Store#": string|number, KI_associatedLocation: string }>
//     isLoading: boolean
//     isError:   boolean
//     error:     Error | null
//   }

async function fetchStoresLambda(body) {
  const response = await fetch(MONGODB_AWSGW_QUERIES, {
    method: "POST",
    mode: "cors",
    credentials: "omit",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": MONGODB_AWSGW_APIKEY,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(
      `useStoresByLocation HTTP ${response.status}: ${response.statusText}`
    );
  }
  const result = await response.json();
  if (result.errorType || result.errorMessage) {
    throw new Error(
      result.errorMessage || result.errorType || "Unknown error"
    );
  }
  return result;
}

export function useStoresByLocation({ locationCode, enabled = true }) {
  const { appEnvirons } = useContext(AppContext);
  const mongoDatabase = appEnvirons?.mongoDb;

  const queryEnabled =
    !!enabled &&
    !!locationCode &&
    typeof locationCode === "string" &&
    !!mongoDatabase;

  const body = useMemo(
    () => ({
      method: "getStoresByLocation",
      dataSource: MONGODB_DATASOURCE_TS,
      database: mongoDatabase,
      collection: MONGODB_COLLECTION_STORES,
      locationCode,
    }),
    [mongoDatabase, locationCode]
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["getStoresByLocation", locationCode],
    queryFn: () => fetchStoresLambda(body),
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  const stores = useMemo(() => {
    const raw = data?.message?.result;
    return Array.isArray(raw) ? raw : [];
  }, [data]);

  return { stores, isLoading: !!isLoading, isError: !!isError, error };
}
